import React, { createContext, useEffect, useReducer } from 'react';

// action - state management
import { LOGIN, LOGOUT } from 'store/actions';
import accountReducer from 'store/accountReducer';

// project imports
import Loader from 'ui-component/shared/Loader';
import { AuthContextType, InitialLoginContextProps } from 'types/type/auth-types';
import { TokenService } from "../utils/axios/token.service";
import axios from "../utils/axios/axiosAuth";
import axiosDomain from "../utils/axios/axiosDomain";
import { User } from "../types/type/users-types";

const initialState: InitialLoginContextProps = {
    isLoggedIn: false,
    isInitialized: false,
    user: null
};

// ==============================|| AUTH CONTEXT & PROVIDER ||============================== //

const AuthContext = createContext<AuthContextType | null>(null);

export const AuthProvider = ({ children }: { children: React.ReactElement }) => {
    const [state, dispatch] = useReducer(accountReducer, initialState);

    const loginFun = (user: User) => {
        dispatch({
            type: LOGIN,
            payload: {
                isLoggedIn: true,
                user
            }
        });
    }

    const logoutFun = () => {
        TokenService.getInstance().clearTokens()
        dispatch({
            type: LOGOUT
        });
    }

    useEffect(() => {
        const init = async () => {
            try {
                let accessToken = TokenService.getInstance().getAccessToken();
                if (accessToken) {
                    const response = await axios.get('/auth/me');
                    if (!response.data.statusCode) {
                        const { instanceId } = response.data;
                        const { data } = await axiosDomain.get('/users/' + instanceId);
                        loginFun(data.result[0]);
                    } else {
                        const response = await axios.get('/auth/refresh');
                        if (!response.data.statusCode) {
                            const { instanceId } = response.data;
                            const { data } = await axiosDomain.get('/users/' + instanceId);
                            loginFun(data.result[0]);
                        } else {
                            logoutFun();
                        }
                    }
                } else {
                    logoutFun();
                }
            } catch (err) {
                logoutFun();
            }
        };
        init()
    }, []);

    const login = async (email: string, password: string) => {
        const response = await axios.post('/auth/login-local', { email, password });
        const { tokens, authEntity } = response.data;
        TokenService.getInstance().setTokens(tokens)
        const { data } = await axiosDomain.get('/users/' + authEntity.instanceId);
        loginFun(data.result[0]);
    };

    const forgotPassword = async (email: string) => {
        await axios.post('/auth/me/reset-token', { recipientAddress: email });
    };

    const resetPassword = async (password: string, token: any) => {
        await axios.post(`/auth/${token}/reset-password`, { newPassword: password });
    };

    const register = async (email: string, password: string) => {
        const body = { email: email, strategySpecificData: { password } }
        try {
            const resp = await axios.post('/auth/register-local', body);
            return resp
        } catch (e: any) {
            return e.response.data
        }

    };

    if (state.isInitialized !== undefined && !state.isInitialized) {
        return <Loader />;
    }

    const logout = async () => {
        logoutFun()
    };

    return (
        <AuthContext.Provider
            value={{
                ...state,
                login,
                logout,
                forgotPassword,
                resetPassword,
                register
            }}
        >
            {children}
        </AuthContext.Provider>
    );
};

export default AuthContext;